import React from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import TitleButtons from "../../../../../components/TitleButtons";

/** ===================== Tipos de API ===================== */
type ApiColaborador = {
  id: number | string;
  nome_completo: string;
  cpf: string;
  data_de_nascimento?: string | null;
  setor?: string | null;
  cargo?: string | null;
  salario?: number | string | null;
  email?: string | null;
  telefone?: string | null;
  contato_de_emergencia?: string | null;
  validade_do_contrato?: string | null;
  criado_em?: string | null;
  atualizado_em?: string | null;
};

type ApiResponse = {
  ok: boolean;
  data?: ApiColaborador | { colaborador?: ApiColaborador };
  erro?: string;
};

type AuthMe = {
  ok: boolean;
  sessao?: {
    uid?: number;
    email: string;
    nome: string;
    picture?: string;
    ts?: number;
    ttl?: number;
  };
};

/** ===================== UI Auxiliar ===================== */
const NoticeBanner: React.FC<{
  type: "success" | "error" | "notify";
  children: React.ReactNode;
}> = ({ type, children }) => {
  let bg = "#e7f1ff",
    color = "#084298",
    border = "#b6d4fe";
  if (type === "success") {
    bg = "#e6ffed";
    color = "#0f5132";
    border = "#badbcc";
  }
  if (type === "error") {
    bg = "#ffecec";
    color = "#842029";
    border = "#f5c2c7";
  }
  return (
    <div
      role="alert"
      style={{
        margin: "12px 40px 0 40px",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border,
        borderStyle: "solid",
        borderWidth: 1,
      }}
    >
      {children}
    </div>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div style={{ marginBottom: "18px" }}>
    <div
      style={{
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: "15px",
        marginBottom: "4px",
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: "Inter", fontWeight: 300, fontSize: "14px" }}>
      {value ?? "—"}
    </div>
  </div>
);

/** ===================== Helpers ===================== */
const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mi = String(d.getUTCMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} - ${hh}:${mi}`;
};

const formatMoneyBR = (v?: number | string | null) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(n);
};

const initialsFromName = (name?: string) => {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
};

const resolvePhotoUrl = (id?: number | string) => {
  if (!id && id !== 0) return null;
  return `https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores/${encodeURIComponent(
    String(id)
  )}/foto`;
};

/** ===================== Página ===================== */
const DetalhesColaborador: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = React.useMemo(() => decodeURIComponent(idParam ?? ""), [idParam]);

  // ---- States
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [colab, setColab] = React.useState<ApiColaborador | null>(null);
  const [authUid, setAuthUid] = React.useState<number | null>(null);
  const [photoOk, setPhotoOk] = React.useState<boolean>(true);

  const navigate = useNavigate();

  // ---- /auth/me (opcional)
  React.useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const res = await fetch("https://api.eva-googlemarine.com/auth/me", {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error(`Falha em /auth/me (HTTP ${res.status})`);
        const json: AuthMe = await res.json();
        const rawUid = json?.sessao?.uid;
        const nextUid: number | null =
          typeof rawUid === "number" && Number.isInteger(rawUid)
            ? rawUid
            : null;
        if (!disposed) setAuthUid(nextUid);
      } catch {
        if (!disposed) setAuthUid(null);
      }
    })();
    return () => {
      disposed = true;
    };
  }, []);

  // ---- Carregar colaborador via POST /colaborador-data-id com { id } no body ----
  const fetchColab = async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    const url =
      "https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaborador-data-id";

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({ id: String(id) }),
        signal,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json: ApiResponse = await res.json();

      const colaborador: ApiColaborador | null =
        (json?.data as any)?.colaborador ?? (json?.data as any) ?? null;

      if (!json?.ok || !colaborador) {
        throw new Error(json?.erro || "Resposta inválida da API");
      }

      setColab(colaborador);
      setPhotoOk(true);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setError(e?.message || "Falha ao buscar colaborador");
        setColab(null);
        setPhotoOk(false);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!id) return;
    const abort = new AbortController();
    fetchColab(abort.signal);
    return () => abort.abort();
  }, [id]);

  if (!id) return <Navigate to="/recursos-humanos/colaboradores" replace />;

  // ====================== Estilos ======================
  const photoRing: React.CSSProperties = {
    width: 120,
    height: 120,
    borderRadius: "50%",
    overflow: "hidden",
    border: "3px solid rgba(18,66,128,0.25)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8fafc",
  };

  const avatarFallback: React.CSSProperties = {
    fontFamily: "Inter, sans-serif",
    fontWeight: 700,
    fontSize: 40,
    color: "#124280",
  };

  const infoCard: React.CSSProperties = {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 20,
    minWidth: 280,
    flex: 1,
  };

  const gridWrap: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
  };

  // ====================== Render ======================
  const nome = colab?.nome_completo ?? "—";
  const fotoUrl = resolvePhotoUrl(colab?.id);

  return (
    <div>
      <TitleButtons
        title={`Departamento Pessoal - Detalhes do Colaborador / ${id}`}
        buttonMarginRight="40px"
        buttons={[
          {
            label: "Editar",
            onClick: () =>
              navigate(`editar-colaborador`, {
                state: { colab }, // pré-preenche instantâneo
                replace: false,
              }),
            variant: "primary",
          },
          {
            label: "Voltar",
            onClick: () => navigate(-1),
            variant: "secondary",
          },
        ]}
      />

      {/* Avisos */}
      {error && (
        <NoticeBanner type="error">
          Erro ao carregar dados do colaborador: {error}
        </NoticeBanner>
      )}
      {!error && !loading && !colab && (
        <NoticeBanner type="error">Colaborador não encontrado.</NoticeBanner>
      )}
      {!error && !loading && colab && !colab.email && (
        <NoticeBanner type="notify">
          Este colaborador ainda não possui <strong>e-mail</strong> cadastrado.
        </NoticeBanner>
      )}
      {!error && !loading && colab && !colab.validade_do_contrato && (
        <NoticeBanner type="notify">
          <strong>Validade do contrato</strong> não informada.
        </NoticeBanner>
      )}

      {/* Perfil */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          padding: "28px 40px 12px 40px",
          flexWrap: "wrap",
        }}
      >
        <div style={photoRing}>
          {photoOk && fotoUrl ? (
            <img
              src={fotoUrl}
              alt={`Foto de ${nome}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={() => setPhotoOk(false)}
            />
          ) : (
            <span style={avatarFallback}>{initialsFromName(nome)}</span>
          )}
        </div>
        <div style={{ minWidth: 240 }}>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: 22,
              lineHeight: 1.2,
            }}
          >
            {loading ? "Carregando..." : nome}
          </div>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 13,
              opacity: 0.8,
              marginTop: 6,
            }}
          >
            ID: {id}
            {colab?.cargo ? ` • ${colab.cargo}` : ""}
            {colab?.setor ? ` • ${colab.setor}` : ""}
          </div>
        </div>
      </div>

      {/* Cards */}
      <div style={{ padding: "16px 40px 40px 40px" }}>
        <div style={gridWrap}>
          <div style={infoCard}>
            <Field label="Nome completo" value={nome} />
            <Field label="CPF" value={colab?.cpf ?? "—"} />
            <Field
              label="Data de nascimento"
              value={formatDate(colab?.data_de_nascimento)}
            />
            <Field label="Setor" value={colab?.setor ?? "—"} />
            <Field label="Cargo" value={colab?.cargo ?? "—"} />
            <Field
              label="Salário (BRL)"
              value={formatMoneyBR(colab?.salario)}
            />
          </div>

          <div style={infoCard}>
            <Field label="E-mail" value={colab?.email ?? "—"} />
            <Field label="Telefone" value={colab?.telefone ?? "—"} />
            <Field
              label="Contato de emergência"
              value={colab?.contato_de_emergencia ?? "—"}
            />
            <Field
              label="Validade do contrato"
              value={formatDate(colab?.validade_do_contrato)}
            />
            <Field label="Criado em" value={formatDateTime(colab?.criado_em)} />
            <Field
              label="Atualizado em"
              value={formatDateTime(colab?.atualizado_em)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalhesColaborador;
